import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Clock, MapPin, Users, Loader2, Calendar } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { eventsApi } from '../services/eventsApi';
import { AuthPromptModal } from '../components/AuthPromptModal';
import { useAuthGate } from '../hooks/useAuthGate';
import { appPath } from '../routes/appPaths';

export default function EventDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    isAuthed,
    userId,
    requireAuth,
    authPromptOpen,
    closeAuthPrompt,
    confirmAuthPrompt,
  } = useAuthGate();

  const { data: event, isLoading } = useQuery({
    queryKey: ['event', id],
    queryFn: async () => {
      const events = await eventsApi.getAll();
      return events.find((e) => e.id === id) ?? null;
    },
    enabled: !!id,
  });

  const { data: registrations = [] } = useQuery({
    queryKey: ['user-registrations', userId],
    queryFn: () => eventsApi.getUserRegistrations(userId!),
    enabled: isAuthed && !!userId,
  });

  const isRegistered = registrations.some((r) => r.eventId === id);

  const registerMutation = useMutation({
    mutationFn: () => eventsApi.register(userId!, id!),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['user-registrations', userId] }),
  });

  const unregisterMutation = useMutation({
    mutationFn: () => eventsApi.unregister(userId!, id!),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['user-registrations', userId] }),
  });

  function handleRegisterClick() {
    requireAuth(appPath(`/events/${id}`), () => {
      if (isRegistered) unregisterMutation.mutate();
      else registerMutation.mutate();
    });
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0a0a0a' }}>
        <Loader2 size={32} className="animate-spin" style={{ color: 'rgba(255,255,255,0.35)' }} />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0a0a0a' }}>
        <p style={{ color: 'rgba(255,255,255,0.4)' }}>Событие не найдено</p>
      </div>
    );
  }

  const dateObj = new Date(event.date);
  const formattedDate = dateObj.toLocaleDateString('ru-RU', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const isPending =
    (registerMutation.isPending && !isRegistered) || (unregisterMutation.isPending && isRegistered);

  const cardStyle = {
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.1)',
  } as const;

  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#0a0a0a', color: '#fff' }}>
      <div className="max-w-md mx-auto w-full flex flex-col flex-1 relative">
        <div className="relative">
          {event.image ? (
            <div className="w-full relative" style={{ paddingTop: '66%' }}>
              <img src={event.image} alt={event.title} className="absolute inset-0 w-full h-full object-cover" />
              <div
                className="absolute inset-0"
                style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.35) 0%, transparent 35%, rgba(10,10,10,0.85) 100%)' }}
              />
            </div>
          ) : (
            <div className="w-full" style={{ paddingTop: '66%', background: 'rgba(255,255,255,0.06)' }} />
          )}

          <button
            onClick={() => navigate(-1)}
            className="absolute top-4 left-4 w-10 h-10 rounded-2xl flex items-center justify-center transition-all active:scale-90"
            style={{
              background: 'rgba(10,10,10,0.7)',
              border: '1px solid rgba(255,255,255,0.15)',
            }}
          >
            <ArrowLeft size={20} style={{ color: '#fff' }} />
          </button>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="flex-1 px-4 pt-5 pb-52"
        >
          <h1
            className="text-2xl font-bold leading-tight mb-4 uppercase"
            style={{ letterSpacing: '-0.03em', fontFamily: 'Unbounded, sans-serif' }}
          >
            {event.title}
          </h1>

          <div className="rounded-3xl p-5 mb-5 space-y-4" style={cardStyle}>
            <DetailRow icon={<Calendar size={18} />} label="Дата" value={formattedDate} />
            {event.time && <DetailRow icon={<Clock size={18} />} label="Время" value={event.time} />}
            {event.location && <DetailRow icon={<MapPin size={18} />} label="Место" value={event.location} />}
            {event.registrationsCount > 0 && (
              <DetailRow
                icon={<Users size={18} />}
                label="Записались"
                value={`${event.registrationsCount} ${declension(event.registrationsCount, ['человек', 'человека', 'людей'])}`}
              />
            )}
          </div>

          {event.description && (
            <div className="rounded-2xl p-4 mb-5" style={cardStyle}>
              <h3
                className="text-xs font-semibold uppercase tracking-wider mb-2"
                style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Manrope, sans-serif' }}
              >
                Описание
              </h3>
              <p className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.7)' }}>
                {event.description}
              </p>
            </div>
          )}
        </motion.div>

        <div
          className="fixed left-0 right-0 max-w-md mx-auto px-4 py-4 z-40"
          style={{
            background: 'linear-gradient(to top, #0a0a0a 70%, transparent)',
            bottom: 'calc(max(16px, env(safe-area-inset-bottom, 0px)) + 88px)',
          }}
        >
          <button
            onClick={handleRegisterClick}
            disabled={isPending}
            className="w-full py-4 rounded-2xl font-semibold text-base transition-all active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2 tracking-[0.06em] uppercase"
            style={
              isRegistered
                ? {
                    background: 'transparent',
                    color: '#fff',
                    border: '1px solid rgba(255,255,255,0.2)',
                    fontFamily: 'Manrope, sans-serif',
                  }
                : { background: '#fff', color: '#0a0a0a', fontFamily: 'Manrope, sans-serif' }
            }
          >
            {isPending ? (
              <Loader2 size={18} className="animate-spin" />
            ) : isRegistered ? (
              'Отменить запись'
            ) : isAuthed ? (
              'Записаться на событие'
            ) : (
              'Войти и записаться'
            )}
          </button>
        </div>
      </div>

      <AuthPromptModal
        open={authPromptOpen}
        onClose={closeAuthPrompt}
        onConfirm={confirmAuthPrompt}
      />
    </div>
  );
}

function DetailRow({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-start gap-3">
      <span className="shrink-0 mt-1" style={{ color: 'rgba(255,255,255,0.5)' }}>{icon}</span>
      <div>
        <p
          className="text-xs font-semibold uppercase tracking-wider"
          style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Manrope, sans-serif' }}
        >
          {label}
        </p>
        <p className="text-sm font-medium mt-1">{value}</p>
      </div>
    </div>
  );
}

function declension(n: number, forms: [string, string, string]): string {
  if (n % 10 === 1 && n % 100 !== 11) return forms[0];
  if (n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20)) return forms[1];
  return forms[2];
}
